import { useEffect, useMemo, useState } from "react";
import { supabase } from "./supabaseClient";

const shortId = value => String(value || "").replace(/[^a-zA-Z0-9]/g, "").slice(-4).toUpperCase() || "????";
export const feedbackIdentityLabel = item => {
  if (!item.user_id) return `Invitado #${shortId(item.anonymous_id)}`;
  return item.username?.trim() || item.user_email?.trim() || `Usuario #${String(item.user_id).slice(0, 8)}`;
};

export default function AdminFeedbackPanel() {
  const [feedback, setFeedback] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [stars, setStars] = useState("all");
  const [origin, setOrigin] = useState("all");
  const [userType, setUserType] = useState("all");
  const [search, setSearch] = useState("");
  const [actionId, setActionId] = useState("");

  const load = async () => {
    setLoading(true); setError("");
    const { data, error: queryError } = await supabase.rpc("admin_get_feedback_history");
    if (queryError) { setError("No se pudo consultar Feedback. Verifica permisos de administrador o RLS: " + queryError.message); setFeedback([]); }
    else setFeedback(data || []);
    setLoading(false);
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const visible = useMemo(() => feedback.filter(item => {
    const type = item.user_id ? "authenticated" : "guest";
    const technicalId = item.user_id || item.anonymous_id || "";
    return (stars === "all" || Number(stars) === Number(item.stars))
      && (origin === "all" || origin === item.origin)
      && (userType === "all" || userType === type)
      && (!search.trim() || technicalId.toLowerCase().includes(search.trim().toLowerCase()));
  }), [feedback, origin, search, stars, userType]);

  const summary = useMemo(() => {
    const included = feedback.filter(item => item.included_in_average !== false);
    const average = included.length ? included.reduce((total, item) => total + Number(item.stars || 0), 0) / included.length : null;
    return { average, included: included.length, excluded: feedback.length - included.length };
  }, [feedback]);

  const toggleAverageInclusion = async item => {
    const currentlyIncluded = item.included_in_average !== false;
    let reason = null;
    if (currentlyIncluded) {
      const response = window.prompt("Motivo de exclusión del promedio (opcional):", item.exclusion_reason || "");
      if (response === null) return;
      reason = response;
    }

    setActionId(item.id);
    const { error: mutationError } = await supabase.rpc("admin_set_feedback_inclusion", {
      p_feedback_id: item.id,
      p_included: !currentlyIncluded,
      p_reason: reason,
    });
    if (mutationError) {
      setError("No se pudo actualizar la inclusión del Feedback: " + mutationError.message);
      setActionId("");
      return;
    }
    await load();
    setActionId("");
  };

  const control = { background: "#111", border: "1px solid #303030", borderRadius: 9, color: "#ccc", padding: "9px 10px", fontSize: 11, minWidth: 0 };
  return <section aria-label="Administración de Feedback">
    <h2 style={{ margin: "0 0 14px", color: "#fff", fontSize: 18 }}>Feedback</h2>

    <div aria-label="Resumen de Feedback" style={{ background: "#111", border: "1px solid #303030", borderRadius: 12, padding: "12px 14px", marginBottom: 12, display: "flex", justifyContent: "space-between", gap: 14, alignItems: "center" }}>
      <div><div style={{ color: "#777", fontSize: 10, textTransform: "uppercase", letterSpacing: 1 }}>Promedio válido</div><strong style={{ color: "#f4c542", fontSize: 22 }}>{summary.average === null ? "—" : summary.average.toFixed(2)} ★</strong></div>
      <div style={{ color: "#888", fontSize: 11, textAlign: "right", lineHeight: 1.5 }}><div>{summary.included} incluidos</div><div>{summary.excluded} excluidos</div></div>
    </div>

    <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 8, marginBottom: 8 }}>
      <select aria-label="Filtrar por estrellas" value={stars} onChange={event => setStars(event.target.value)} style={control}><option value="all">Todas ★</option>{[1,2,3,4,5].map(value => <option key={value} value={value}>{value} ★</option>)}</select>
      <select aria-label="Filtrar por origen" value={origin} onChange={event => setOrigin(event.target.value)} style={control}><option value="all">Todo origen</option><option value="manual_menu">Menú manual</option><option value="automatic_invitation">Invitación</option></select>
      <select aria-label="Filtrar por tipo de usuario" value={userType} onChange={event => setUserType(event.target.value)} style={control}><option value="all">Todos</option><option value="authenticated">Autenticado</option><option value="guest">Invitado</option></select>
    </div>
    <input aria-label="Buscar identificador técnico" value={search} onChange={event => setSearch(event.target.value)} placeholder="Buscar user_id / guest_id" style={{ ...control, width: "100%", boxSizing: "border-box", marginBottom: 16 }} />

    {loading && <p role="status" style={{ color: "#888", fontSize: 13 }}>Cargando Feedback…</p>}
    {error && <div role="alert" style={{ background: "#321717", border: "1px solid #5a2d2d", color: "#ef9a9a", borderRadius: 10, padding: 12, fontSize: 12 }}><p style={{ margin: "0 0 8px" }}>{error}</p><button onClick={load} style={{ background: "#e53935", border: 0, borderRadius: 8, color: "#fff", padding: "7px 12px", cursor: "pointer" }}>Reintentar</button></div>}
    {!loading && !error && visible.length === 0 && <p style={{ color: "#666", fontSize: 13 }}>{feedback.length ? "No hay Feedback que coincida con los filtros." : "Aún no hay Feedback registrado."}</p>}
    {!loading && !error && <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {visible.map(item => {
        const authenticated = !!item.user_id;
        const technicalId = item.user_id || item.anonymous_id || "—";
        const identity = feedbackIdentityLabel(item);
        const included = item.included_in_average !== false;
        return <article key={item.id} style={{ background: "#111", border: `1px solid ${included ? "#292929" : "#5a3a22"}`, borderRadius: 13, padding: "13px 14px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 8, marginBottom: 8 }}><strong style={{ color: "#f4c542", fontSize: 15 }}>{"★".repeat(item.stars)}<span style={{ color: "#444" }}>{"★".repeat(5 - item.stars)}</span></strong><time style={{ color: "#666", fontSize: 10 }}>{new Date(item.submitted_at).toLocaleString()}</time></div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginBottom: 8 }}>{(item.chips || []).map(chip => <span key={chip} style={{ background: "#242424", border: "1px solid #343434", borderRadius: 14, padding: "4px 8px", color: "#aaa", fontSize: 10 }}>{chip}</span>)}</div>
          <p style={{ margin: "0 0 9px", color: item.comment ? "#ccc" : "#555", fontSize: 12, lineHeight: 1.45 }}>{item.comment || "Sin comentario"}</p>
          <div style={{ marginBottom: 9, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
            <span style={{ color: included ? "#81c784" : "#ffb74d", fontSize: 10, fontWeight: 700 }}>{included ? "Incluido en promedio" : "Excluido del promedio"}</span>
            <button disabled={actionId === item.id} onClick={() => toggleAverageInclusion(item)} style={{ background: "#202020", border: "1px solid #3a3a3a", borderRadius: 8, color: "#ddd", padding: "6px 9px", fontSize: 10, cursor: actionId === item.id ? "wait" : "pointer" }}>{actionId === item.id ? "Guardando…" : included ? "Excluir del promedio" : "Incluir en promedio"}</button>
          </div>
          {!included && <div style={{ color: "#9a7a61", fontSize: 10, marginBottom: 8 }}>Motivo: {item.exclusion_reason || "Sin motivo indicado"}</div>}
          <div style={{ borderTop: "1px solid #242424", paddingTop: 8, color: "#777", fontSize: 10, lineHeight: 1.6 }}>
            <div><strong style={{ color: "#bbb", fontSize: 12 }}>{identity}</strong> · {authenticated ? "Autenticado" : "Invitado"} · {item.origin}</div>
            <div style={{ wordBreak: "break-all", color: "#555" }}>{authenticated ? "user_id" : "guest_id"}: {technicalId}</div>
            <div>Contexto: {JSON.stringify(item.screen_context || {})}</div>
          </div>
        </article>;
      })}
    </div>}
  </section>;
}
